/* eslint-disable react/jsx-no-bind */
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Stack,
  Typography,
} from '@mui/material';
import { VideocamOutlined } from '@mui/icons-material';
import CreativeEditor from '@cesdk/cesdk-js/react';
import { useConfirm } from 'material-ui-confirm';
import {
  disposeVideoPlaceholdersCreatorEditor,
  initVideoPlaceholdersCreatorEditor,
} from '../imgly/index.ts';
import AppApi from '../shared/App.api';
import SpinningSpark from '../shared/components/SpinningSpark';
import adminFormStyles from '../shared/styles/adminForm.styles';
import styleColors from '../colors.styles';
import EditableVideoHelpPanel from './EditableVideoHelpPanel';
import TemplatesApi from './Templates.api';
import {
  getEditableVideoSceneSizeLimitMessage,
  isEditableVideoSceneSizeError,
  isEditableVideoSceneTooLarge,
  loadEditableVideoScene,
  readEditableVideoSceneMetadata,
} from './editableVideo.utils';
import { AlertsContext } from '../shared/alerts/Alerts.context';

const HELP_CLOSE_DELAY_MS = 150;

const styles = {
  paper: {
    borderRadius: '12px',
    height: '95vh',
    maxHeight: '95vh',
    maxWidth: 'none',
    overflow: 'hidden',
    width: '95vw',
  },
  layout: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: 72,
    px: 3,
    py: 2,
  },
  titleRow: {
    alignItems: 'center',
    color: '#041f41',
    display: 'flex',
    gap: 1.5,
  },
  titleIconWrap: {
    alignItems: 'center',
    backgroundColor: '#e5eefc',
    borderRadius: '50%',
    color: styleColors.base.walmart_blue,
    display: 'flex',
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  titleIcon: {
    fontSize: 22,
  },
  title: {
    color: '#041f41',
    fontSize: '20px',
    fontWeight: 800,
  },
  helpButton: {
    backgroundColor: styleColors.base.light,
    borderColor: '#e9ecee',
    borderRadius: '8px',
    color: '#041f41',
    fontSize: '14px',
    fontWeight: 600,
    minHeight: 40,
    minWidth: 80,
    px: 3,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: styleColors.base.light,
      borderColor: '#dbe2e6',
    },
  },
  body: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    pb: 2,
    px: 3,
  },
  editorWrap: {
    backgroundColor: '#111827',
    borderRadius: '12px',
    clipPath: 'inset(0 round 12px)',
    flex: 1,
    isolation: 'isolate',
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
    position: 'relative',
  },
  editorInner: {
    borderRadius: '12px',
    height: '100%',
    overflow: 'hidden',
    width: '100%',
    '& > div': {
      borderRadius: '12px',
      height: '100% !important',
      overflow: 'hidden',
    },
  },
  helpOverlay: {
    maxWidth: 420,
    pointerEvents: 'auto',
    position: 'absolute',
    width: 420,
    zIndex: 10,
  },
  editorLoading: {
    alignItems: 'center',
    backgroundColor: '#111827',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: 1,
  },
  footer: {
    gap: 1.5,
    justifyContent: 'flex-end',
    px: 3,
    py: 2,
  },
  discardButton: adminFormStyles.secondaryButton,
  saveButton: adminFormStyles.primaryButton,
};

export default function EditableVideoModal({
  open,
  onClose,
  onSave,
  initialSceneUrl,
}) {
  const confirm = useConfirm();
  const { showAlert } = useContext(AlertsContext);
  const showAlertRef = useRef(showAlert);
  showAlertRef.current = showAlert;
  const cesdkRef = useRef(null);
  const helpCloseTimeoutRef = useRef(null);
  const layoutRef = useRef(null);
  const helpButtonRef = useRef(null);
  const bodyRef = useRef(null);
  const footerRef = useRef(null);
  const [licenseKey, setLicenseKey] = useState(null);
  const [licenseError, setLicenseError] = useState(false);
  const [editorLoading, setEditorLoading] = useState(true);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpOverlayStyle, setHelpOverlayStyle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [maxVideoUploadSize, setMaxVideoUploadSize] = useState(null);
  const [isSceneReady, setIsSceneReady] = useState(false);
  const [initFailed, setInitFailed] = useState(false);
  const [editorSessionKey, setEditorSessionKey] = useState(0);
  const isEditingExistingScene = Boolean(initialSceneUrl);

  const updateHelpOverlayPosition = useCallback(() => {
    if (
      !layoutRef.current ||
      !helpButtonRef.current ||
      !bodyRef.current ||
      !footerRef.current
    ) {
      return;
    }

    const layoutRect = layoutRef.current.getBoundingClientRect();
    const buttonRect = helpButtonRef.current.getBoundingClientRect();
    const bodyRect = bodyRef.current.getBoundingClientRect();
    const footerRect = footerRef.current.getBoundingClientRect();

    setHelpOverlayStyle({
      bottom: layoutRect.bottom - footerRect.top + 16,
      right: layoutRect.right - buttonRect.right,
      top: bodyRect.top - layoutRect.top,
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setHelpOpen(false);
      setEditorLoading(true);
      setSaving(false);
      setMaxVideoUploadSize(null);
      setIsSceneReady(false);
      setInitFailed(false);
      disposeVideoPlaceholdersCreatorEditor();
      cesdkRef.current = null;
      return undefined;
    }

    setEditorSessionKey((currentKey) => currentKey + 1);

    let cancelled = false;

    async function loadEditorConfig() {
      setLicenseError(false);
      setLicenseKey(null);
      setMaxVideoUploadSize(null);

      try {
        const licenseResponse = await AppApi.get(
          '/admin/web-cesdk-license-key'
        );

        if (!cancelled) {
          setLicenseKey(licenseResponse.data.licenseKey);
        }
      } catch (error) {
        console.error('Error loading CE.SDK license key:', error);
        if (!cancelled) {
          setLicenseError(true);
        }
        return;
      }

      try {
        const uploadOptions =
          await TemplatesApi.getEditableVideoSceneUploadOptions();

        if (!cancelled) {
          setMaxVideoUploadSize(uploadOptions.maxVideoUploadSize);
        }
      } catch (error) {
        console.warn('Failed to load editable video upload options:', error);
      }
    }

    loadEditorConfig();

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(
    () => () => {
      if (helpCloseTimeoutRef.current) {
        clearTimeout(helpCloseTimeoutRef.current);
      }
    },
    []
  );

  useLayoutEffect(() => {
    if (!open || !helpOpen) {
      return undefined;
    }

    updateHelpOverlayPosition();

    window.addEventListener('resize', updateHelpOverlayPosition);

    return () => {
      window.removeEventListener('resize', updateHelpOverlayPosition);
    };
  }, [helpOpen, open, updateHelpOverlayPosition]);

  const initEditor = useCallback(
    async (cesdk) => {
      setIsSceneReady(false);
      setInitFailed(false);
      cesdkRef.current = null;

      try {
        const shouldLoadExistingScene = Boolean(initialSceneUrl);

        await initVideoPlaceholdersCreatorEditor(cesdk, {
          skipEmptyScene: shouldLoadExistingScene,
        });

        if (shouldLoadExistingScene) {
          await loadEditableVideoScene(cesdk, initialSceneUrl);
        }

        cesdkRef.current = cesdk;
        setIsSceneReady(true);
      } catch (error) {
        console.error('Failed to initialize editable video editor:', error);
        cesdkRef.current = null;
        setInitFailed(true);
        showAlertRef.current(
          'Unable to load the saved video template.',
          'error'
        );
        throw error;
      }
    },
    [initialSceneUrl]
  );

  const handleEditorError = useCallback((error) => {
    console.error('Editable video editor failed:', error);
    cesdkRef.current = null;
    setIsSceneReady(false);
    setInitFailed(true);
    showAlertRef.current(
      'Unable to load the video editor. Please try again.',
      'error'
    );
  }, []);

  const editorConfig = useMemo(
    () => ({
      license: licenseKey,
      baseURL: '/assets',
    }),
    [licenseKey]
  );

  const handleEditorLoadingStateChange = useCallback((state) => {
    setEditorLoading(state === 'loading' || state === 'idle');
  }, []);

  function openHelp() {
    if (helpCloseTimeoutRef.current) {
      clearTimeout(helpCloseTimeoutRef.current);
      helpCloseTimeoutRef.current = null;
    }
    setHelpOpen(true);
    updateHelpOverlayPosition();
  }

  function scheduleCloseHelp() {
    if (helpCloseTimeoutRef.current) {
      clearTimeout(helpCloseTimeoutRef.current);
    }
    helpCloseTimeoutRef.current = setTimeout(() => {
      setHelpOpen(false);
    }, HELP_CLOSE_DELAY_MS);
  }

  async function handleClose() {
    try {
      await confirm({
        title: isEditingExistingScene
          ? 'Discard changes?'
          : 'Are you sure you want to cancel?',
        description: isEditingExistingScene
          ? 'Your video template changes will be lost if you leave the editor.'
          : 'By leaving the editor, you will lose your progress.',
        confirmationText: isEditingExistingScene ? 'Discard changes' : 'Yes',
        cancellationText: isEditingExistingScene ? 'Keep editing' : 'No',
      });
      onClose();
    } catch {
      // User chose to keep editing.
    }
  }

  async function handleSave() {
    if (!cesdkRef.current || saving || !isSceneReady || initFailed) {
      return;
    }

    setSaving(true);

    try {
      const cesdkSceneMetadata = readEditableVideoSceneMetadata(
        cesdkRef.current
      );
      const archiveBlob = await cesdkRef.current.engine.scene.saveToArchive();
      const archiveFile = new File(
        [archiveBlob],
        'editable-video-template.zip',
        { type: 'application/zip' }
      );

      if (
        maxVideoUploadSize &&
        isEditableVideoSceneTooLarge(archiveFile.size, maxVideoUploadSize)
      ) {
        showAlert(
          getEditableVideoSceneSizeLimitMessage(maxVideoUploadSize),
          'error'
        );
        return;
      }

      const { url: cesdkSceneUrl, picture } =
        await TemplatesApi.uploadEditableVideoScene(archiveFile);

      onSave({
        cesdk_scene_url: cesdkSceneUrl,
        cesdk_scene_metadata: cesdkSceneMetadata,
        type: 'video',
        picture,
      });
      onClose();
    } catch (error) {
      console.error('Failed to save editable video template:', error);

      if (isEditableVideoSceneSizeError(error) && maxVideoUploadSize) {
        showAlert(
          getEditableVideoSceneSizeLimitMessage(maxVideoUploadSize),
          'error'
        );
        return;
      }

      showAlert('Failed to save editable video. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  function handleDialogClose(_event, reason) {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  }

  function renderEditor() {
    if (licenseError) {
      return (
        <Box sx={styles.editorLoading}>
          <Typography color="error">
            Unable to load the video editor. Please try again.
          </Typography>
        </Box>
      );
    }

    if (!licenseKey) {
      return (
        <Box sx={styles.editorLoading}>
          <SpinningSpark marginTop="0" />
        </Box>
      );
    }

    if (initFailed) {
      return (
        <Box sx={styles.editorLoading}>
          <Typography color="error">
            Unable to load the saved video template.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={styles.editorInner}>
        {(editorLoading || !isSceneReady) && (
          <Box sx={styles.editorLoading}>
            <SpinningSpark marginTop="0" />
          </Box>
        )}
        <CreativeEditor
          key={`${editorSessionKey}-${
            initialSceneUrl || 'new-editable-video-scene'
          }`}
          config={editorConfig}
          init={initEditor}
          width="100%"
          height="100%"
          onLoadingStateChange={handleEditorLoadingStateChange}
          onError={handleEditorError}
        />
      </Box>
    );
  }

  const saveButtonLabel = isEditingExistingScene
    ? 'Save'
    : 'Save & add to template';

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={false}
      PaperProps={{ sx: styles.paper }}
      aria-labelledby="editable-video-modal-title"
    >
      <Box ref={layoutRef} sx={styles.layout}>
        <Box sx={styles.header}>
          <Stack direction="row" alignItems="center" sx={styles.titleRow}>
            <Box sx={styles.titleIconWrap}>
              <VideocamOutlined sx={styles.titleIcon} />
            </Box>
            <Typography
              component="h2"
              id="editable-video-modal-title"
              sx={styles.title}
            >
              Editable video maker
            </Typography>
          </Stack>

          <Button
            ref={helpButtonRef}
            type="button"
            variant="outlined"
            sx={styles.helpButton}
            onMouseEnter={openHelp}
            onMouseLeave={scheduleCloseHelp}
            onFocus={openHelp}
            onBlur={scheduleCloseHelp}
          >
            Help
          </Button>
        </Box>

        <Box ref={bodyRef} sx={styles.body}>
          {open && <Box sx={styles.editorWrap}>{renderEditor()}</Box>}
        </Box>

        {helpOpen && helpOverlayStyle && (
          <Box
            sx={{ ...styles.helpOverlay, ...helpOverlayStyle }}
            onMouseEnter={openHelp}
            onMouseLeave={scheduleCloseHelp}
          >
            <EditableVideoHelpPanel />
          </Box>
        )}

        <Stack ref={footerRef} direction="row" sx={styles.footer}>
          <Button
            type="button"
            variant="outlined"
            onClick={handleClose}
            disabled={saving}
            sx={styles.discardButton}
          >
            {isEditingExistingScene ? 'Discard changes' : 'Cancel'}
          </Button>
          <Button
            type="button"
            variant="contained"
            disabled={
              saving ||
              !isSceneReady ||
              initFailed ||
              licenseError ||
              !licenseKey
            }
            sx={styles.saveButton}
            onClick={handleSave}
          >
            {saving ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              saveButtonLabel
            )}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}

EditableVideoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialSceneUrl: PropTypes.string,
};

EditableVideoModal.defaultProps = {
  initialSceneUrl: null,
};
